import { BaseEntityAdapter } from "@brains/plugins";
import type {
  BaseEntity,
  BaseEntityFrontmatterSchema,
  EntitySchema,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import {
  actionItemMetadataSchema,
  actionItemSchema,
  decisionMetadataSchema,
  decisionSchema,
  type ActionItemEntity,
  type ActionItemMetadata,
  type DecisionEntity,
  type DecisionMetadata,
} from "../schemas/conversation-memory";
import {
  ACTION_ITEM_ENTITY_TYPE,
  DECISION_ENTITY_TYPE,
} from "../lib/constants";

const frontmatterRecordSchema = z.record(z.string(), z.unknown());

interface TypedFrontmatterSchema<T> {
  parse(data: unknown): T;
}

/**
 * Shared adapter for derived conversation memory entities (decisions,
 * action items). Both entity types share an identical markdown shape:
 * the body is a single H1 title followed by free text, with metadata
 * persisted as YAML frontmatter.
 */
class ConversationMemoryEntityAdapter<
  TEntity extends BaseEntity<TMetadata>,
  TMetadata extends object,
> extends BaseEntityAdapter<TEntity, TMetadata, Record<string, unknown>> {
  private readonly metadataSchema: { parse(data: unknown): TMetadata };

  constructor(config: {
    entityType: string;
    purpose: string;
    schema: EntitySchema<TEntity>;
    metadataSchema: BaseEntityFrontmatterSchema<Record<string, unknown>>;
    parseMetadata: TypedFrontmatterSchema<TMetadata>;
  }) {
    super({
      entityType: config.entityType,
      purpose: config.purpose,
      schema: config.schema,
      frontmatterSchema: config.metadataSchema,
    });
    this.metadataSchema = config.parseMetadata;
  }

  public composeContent(
    title: string,
    text: string,
    metadata: TMetadata,
  ): string {
    return this.buildMarkdown(
      [`# ${title}`, "", text.trim(), ""].join("\n"),
      frontmatterRecordSchema.parse(metadata),
    );
  }

  public override toMarkdown(entity: TEntity): string {
    return entity.content;
  }

  public fromMarkdown(markdown: string): Partial<TEntity> {
    // Irreducible: no object literal is assignable to `Partial<TEntity>` while
    // TEntity is unresolved, because a subclass may narrow any of these fields.
    // Carrying the literal entity type through the constructor is not enough —
    // tried, and it still reports `{ entityType: TEntity["entityType"];
    // content: string; metadata: TMetadata }` as unassignable, since `content`
    // and `metadata` are unprovable for the same reason. Removing it needs
    // TEntity resolved, which means dropping the shared base entirely.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- see the note above: no object literal is assignable to Partial<TEntity> while TEntity is unresolved
    return {
      entityType: this.entityType,
      content: markdown,
      metadata: this.parseFrontMatter(markdown, this.metadataSchema),
    } as Partial<TEntity>;
  }
}

export class DecisionAdapter extends ConversationMemoryEntityAdapter<
  DecisionEntity,
  DecisionMetadata
> {
  constructor() {
    super({
      entityType: DECISION_ENTITY_TYPE,
      purpose: "A decision recorded from a conversation.",
      schema: decisionSchema,
      metadataSchema: decisionMetadataSchema,
      parseMetadata: decisionMetadataSchema,
    });
  }
}

export class ActionItemAdapter extends ConversationMemoryEntityAdapter<
  ActionItemEntity,
  ActionItemMetadata
> {
  constructor() {
    super({
      entityType: ACTION_ITEM_ENTITY_TYPE,
      purpose: "An action item captured from a conversation.",
      schema: actionItemSchema,
      metadataSchema: actionItemMetadataSchema,
      parseMetadata: actionItemMetadataSchema,
    });
  }
}
